import type { ComponentType, SVGProps } from 'react';
import {
  AudioLinesIcon,
  CropIcon,
  FileIcon,
  FilmIcon,
  GaugeIcon,
  ImageIcon,
  PaletteIcon,
  TimerIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  SpecMargins,
  SpecRatioEntry,
  SpecRawValue,
  SpecSize,
  SpecValueKind,
} from '@inference.engine/ad-formats/web';
import {
  diagramFor,
  fitAspect,
  ratioValue,
  type Rect,
  type SpatialDiagram,
  type StaticDiagram,
} from '../lib/spec-diagram';

const VIEW = { width: 240, height: 150, padding: 8 };
const AREA: Rect = {
  x: VIEW.padding,
  y: VIEW.padding,
  width: VIEW.width - VIEW.padding * 2,
  height: VIEW.height - VIEW.padding * 2,
};

const STATIC_ICONS: Record<StaticDiagram, ComponentType<SVGProps<SVGSVGElement>>> = {
  file: FileIcon,
  color: PaletteIcon,
  duration: TimerIcon,
  frameRate: FilmIcon,
  bitrate: GaugeIcon,
  audio: AudioLinesIcon,
  cropping: CropIcon,
  thumbnail: ImageIcon,
};

interface SpecDiagramProps {
  kind: SpecValueKind;
  property: string;
  /** What the inputs hold right now, or null while they do not parse. */
  value: SpecRawValue | null;
  canvas: { w: number; h: number };
}

/** What the parameter being edited refers to, drawn to scale against the format's canvas. */
export function SpecDiagram({ kind, property, value, canvas }: SpecDiagramProps) {
  const { t } = useTranslation('admin');
  const diagram = diagramFor(kind, property);

  if (diagram.type === 'static') {
    const Icon = STATIC_ICONS[diagram.diagram];
    return (
      <figure className="flex items-center gap-md rounded-md border border-border bg-muted p-md">
        <Icon aria-hidden className="size-10 shrink-0 text-muted-foreground" />
        <figcaption className="text-body-sm text-muted-foreground">
          {staticCaption(t, diagram.diagram)}
        </figcaption>
      </figure>
    );
  }

  const caption = spatialCaption(t, diagram.diagram);
  return (
    <figure className="flex flex-col gap-xs rounded-md border border-border bg-muted p-md">
      <svg
        role="img"
        aria-label={caption}
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        className="h-auto w-full max-w-72 self-center"
      >
        <SpatialShapes diagram={diagram.diagram} value={value} canvas={canvas} />
      </svg>
      <figcaption className="text-body-sm text-muted-foreground">{caption}</figcaption>
      <ul className="flex flex-wrap gap-x-md gap-y-2xs text-body-sm text-muted-foreground">
        <li className="flex items-center gap-2xs">
          <span
            aria-hidden
            className="size-3 rounded-sm border border-foreground/40 bg-background"
          />
          {t('admin.adFormats.diagram.canvas', { width: canvas.w, height: canvas.h })}
        </li>
        <li className="flex items-center gap-2xs">
          <span aria-hidden className="size-3 rounded-sm border border-primary bg-primary/15" />
          {diagram.diagram === 'safeZone'
            ? t('admin.adFormats.diagram.safeArea')
            : t('admin.adFormats.diagram.value')}
        </li>
      </ul>
    </figure>
  );
}

function SpatialShapes({
  diagram,
  value,
  canvas,
}: {
  diagram: SpatialDiagram;
  value: SpecRawValue | null;
  canvas: { w: number; h: number };
}) {
  if (diagram === 'size') {
    const size = value as SpecSize | null;
    const asset = { w: size?.width ?? canvas.w, h: size?.height ?? canvas.h };
    const scale = Math.min(
      AREA.width / Math.max(canvas.w, asset.w),
      AREA.height / Math.max(canvas.h, asset.h),
    );
    const centred = (w: number, h: number): Rect => ({
      x: AREA.x + (AREA.width - w * scale) / 2,
      y: AREA.y + (AREA.height - h * scale) / 2,
      width: w * scale,
      height: h * scale,
    });
    const partial = !size || size.width === null || size.height === null;
    return (
      <>
        <CanvasShape rect={centred(canvas.w, canvas.h)} />
        {size && <ValueShape rect={centred(asset.w, asset.h)} dashed={partial} />}
      </>
    );
  }

  const frame = fitAspect(AREA, canvas.w / canvas.h);

  if (diagram === 'safeZone') {
    const margins = value as SpecMargins | null;
    const scale = frame.width / canvas.w;
    const inset = margins
      ? {
          x: frame.x + Math.min(margins.left, canvas.w) * scale,
          y: frame.y + Math.min(margins.top, canvas.h) * scale,
          width: Math.max(0, canvas.w - margins.left - margins.right) * scale,
          height: Math.max(0, canvas.h - margins.top - margins.bottom) * scale,
        }
      : undefined;
    return (
      <>
        <CanvasShape rect={frame} />
        {inset && <ValueShape rect={inset} dashed />}
      </>
    );
  }

  const ratios =
    diagram === 'ratio'
      ? typeof value === 'string'
        ? [value]
        : []
      : ((value as readonly SpecRatioEntry[] | null) ?? [])
          .flatMap((entry) => (typeof entry === 'string' ? [entry] : [entry.from, entry.to]))
          .slice(0, 6);
  return (
    <>
      <CanvasShape rect={frame} />
      {ratios.map((ratio) => {
        const aspect = ratioValue(ratio);
        return aspect ? <ValueShape key={ratio} rect={fitAspect(frame, aspect)} /> : null;
      })}
    </>
  );
}

function CanvasShape({ rect }: { rect: Rect }) {
  return <rect {...rect} className="fill-background stroke-foreground/40" strokeWidth={1.5} />;
}

function ValueShape({ rect, dashed }: { rect: Rect; dashed?: boolean }) {
  return (
    <rect
      {...rect}
      className="fill-primary/15 stroke-primary"
      strokeWidth={1.5}
      strokeDasharray={dashed ? '4 3' : undefined}
    />
  );
}

type AdminT = ReturnType<typeof useTranslation<'admin'>>['t'];

function spatialCaption(t: AdminT, diagram: SpatialDiagram): string {
  switch (diagram) {
    case 'size':
      return t('admin.adFormats.diagram.captions.size');
    case 'ratio':
      return t('admin.adFormats.diagram.captions.ratio');
    case 'ratioList':
      return t('admin.adFormats.diagram.captions.ratioList');
    case 'safeZone':
      return t('admin.adFormats.diagram.captions.safeZone');
  }
}

function staticCaption(t: AdminT, diagram: StaticDiagram): string {
  switch (diagram) {
    case 'file':
      return t('admin.adFormats.diagram.captions.file');
    case 'color':
      return t('admin.adFormats.diagram.captions.color');
    case 'duration':
      return t('admin.adFormats.diagram.captions.duration');
    case 'frameRate':
      return t('admin.adFormats.diagram.captions.frameRate');
    case 'bitrate':
      return t('admin.adFormats.diagram.captions.bitrate');
    case 'audio':
      return t('admin.adFormats.diagram.captions.audio');
    case 'cropping':
      return t('admin.adFormats.diagram.captions.cropping');
    case 'thumbnail':
      return t('admin.adFormats.diagram.captions.thumbnail');
  }
}
